import {
  ChangeDetectionStrategy,
  Component, EventEmitter, Inject,
  Input, OnChanges, OnInit, Output,
  SimpleChanges
} from "@angular/core";
import {VisitPlanModel} from "../../../models/visit-plan.model";
import {VisitPlanCellModel} from "../../../models/visit-plan-cell.model";
import {FieldValueModel} from "../../../models/field-value.model";
import {CellEditDialogComponent} from "../cell-edit-dialog/cell-edit-dialog.component";
import {PanelService} from "../../../../commons/angular/services/panel.service";
import {VisitPlanCallModel} from "../../../models/visit-plan-call.model";
import {isNull, isUndefined} from "lodash";
import {VisitScheduleDayModel} from "../../../models/visit-schedule-day.model";
import {
  findVisitScheduleDayByDate,
  isVisitScheduleDayDispatched
} from "../../../models/visit-schedule-day.functions";
import * as moment from "moment";
import {
  VisitPlanAccountAnalysisModel, VisitPlanAccountCallAnalysisModel,
  VisitPlanAccountSlotAnalysisModel
} from "../../../models/visit-plan-account-analysis.model";

require('./visit-plan-accounts-table-row-cell.scss');
@Component({
  standalone: false,
  selector: 'app-visit-plan-accounts-table-row-cell',
  templateUrl: './visit-plan-accounts-table-row-cell.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VisitPlanAccountsTableRowCellComponent implements OnInit, OnChanges {

  @Input() cell: VisitPlanCellModel;
  @Input() cellTracker: VisitPlanCellModel;
  @Input() visitPlan: VisitPlanModel;
  @Input() programTypeAliasMap: { [key: string]: string };
  @Input() visitScheduleDays: VisitScheduleDayModel[];
  @Input() visitScheduleDay: VisitScheduleDayModel;
  @Input() visitPlanAccountCellAnalysis: VisitPlanAccountSlotAnalysisModel;
  @Input() dayColWidth: number;
  @Input() tableFilterColumns: { isDisplayDeliveryEtd: boolean, isDisplayDeliveryUnits: boolean, isDisplayCallTypes: boolean, isDisplayServiceTime: boolean};
  @Output() onDeleteCalls = new EventEmitter<{ deletedCallIds: string[], cellId: string }>();
  @Output() onUpdateVisitCellTracker = new EventEmitter<VisitPlanCellModel>;
  @Output() onUpdateCallsTimeWindow = new EventEmitter<{
    list: { callId: string, notBefore: number, notAfter: number }[],
    cellId: string
  }>();
  @Output() onAddCall = new EventEmitter<{ cellId: string, date: number }>();
  @Output() onAdvanceEditCall = new EventEmitter<{
    cellId: string,
    call: VisitPlanCallModel,
    visitPlanCell: VisitPlanCellModel
  }>();
  @Output() onAssignCallsToResource = new EventEmitter<{
    callIds: string[],
    resourceId: string,
    cellId: string,
    accountId: string,
    dateRange: { from: number, to: number }
  }>();
  @Output() onUnAssignCalls = new EventEmitter<[]>();
  @Output() onLockCallChange = new EventEmitter<{ id: string, locked: boolean }>();

  public cellTotals: {
    merchandiseCount: number;
    ordersCount: number;
    salesCount: number;
    initialServiceTime: number;
    adjustedServiceTime: number | undefined;
    merchandiseUnitsCount: number;
    hasAdjustedServiceTime: boolean;
  };
  public hasDelivery: boolean;
  public isShowEditBtn: boolean;
  public cellCopy: VisitPlanCellModel;
  public canEdit: boolean;
  public isDayDispatched: boolean;
  public visitPlanAccountCallAnalysisMap: { [key: string]: VisitPlanAccountCallAnalysisModel };
  public isDeliveryEtdNextDay: boolean;
  public isDeliveryEtdPreviousDay: boolean;

  constructor(@Inject(PanelService) private panelService: PanelService) {
  }

  ngOnInit() {
    this.initCellTotals();
    this._setCellDeliveryEtdWarning();
    this.canEdit = (this.visitScheduleDay) ?
      moment(this.visitScheduleDay.date).endOf('day').valueOf() > moment().valueOf() && this.visitScheduleDay.status !== 'Completed' :
      false;
    this._setIsDayDispatched();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.cell && !changes.cell.isFirstChange()) {
      this.initCellTotals();
      this._setCellDeliveryEtdWarning();
      this._setIsDayDispatched();
    }

    if (changes.visitScheduleDays && !changes.visitScheduleDays.isFirstChange()) {
      this._setIsDayDispatched();
    }

    if (changes.visitPlanAccountCellAnalysis) {
      this.visitPlanAccountCallAnalysisMap = {}
      this.visitPlanAccountCellAnalysis?.calls?.forEach((call) => {
        this.visitPlanAccountCallAnalysisMap[call.callId] = call;
      })
    }

    if (changes.visitScheduleDay) {
      this.canEdit = (this.visitScheduleDay) ?
        moment(this.visitScheduleDay.date).endOf('day').valueOf() > moment().valueOf() && this.visitScheduleDay.status !== 'Completed' :
        false;
    }
  }

  onShowCellEditDialog(event, isDeliveryEdit?: boolean) {
    // A deleted (archived) project is read only.
    // The dialog assigns calls to resources on its own, so it must not be opened at all.
    if (this.visitPlan?.isArchive) return;

    const panel = this.panelService.open(CellEditDialogComponent, event, {
      visitPlanCell: this.cell,
      visitPlanCellTracker: this.cellTracker,
      programTypeAliasMap: this.programTypeAliasMap,
      dateRange: {from: this.visitPlan.from, to: this.visitPlan.to},
      isDeliveryEdit: isDeliveryEdit,
      visitPlan: this.visitPlan,
      visitScheduleDays: this.visitScheduleDays,
      isDayDispatched: this.isDayDispatched,
      visitPlanAccountCellAnalysis: this.visitPlanAccountCellAnalysis,
      isServiceTimeFromMLPrediction: this.visitPlan['visitPlanConfig'].visitSlotProviderTypeRegistry.visitSlotServiceTimeProviderType.some((providerType) => providerType === "MDLZ_PREDICTION")
    }, {
      height: 300
    })

    if (panel) {
      if (panel.instance?.onLockCallChange) {
        panel.instance.onLockCallChange.subscribe((data: { id: string, locked: boolean }) => {
          this.onLockCallChange.emit(data);
        });
      }


      panel.afterClose$.subscribe((data) => {
        if (!!data?.advanceEdit) {
          this.onAdvanceEditCall.emit({cellId: this.cell.id, call: data.call, visitPlanCell: data.visitPlanCell});
        } else if (!!data?.visitPlanCell) {
          this.onUpdateVisitCellTracker.emit(data.visitPlanCell);
        } else if (!!data?.deletedCallIds?.length) {
          this.onDeleteCalls.emit({deletedCallIds: data.deletedCallIds, cellId: this.cell.id});
        } else if (!!data?.updateCallTimeWindowList) {
          this.onUpdateCallsTimeWindow.emit({list: data.updateCallTimeWindowList, cellId: this.cell.id});
        } else if (!!data?.onAddCall) {
          this.onAddCall.emit({cellId: this.cell.id, date: this.cell.date});
        } else if (!!data?.assignedCallsToResource) {
          this.onAssignCallsToResource.emit(data.assignedCallsToResource);
        } else if (!!data?.unassignedPayload) {
          this.onUnAssignCalls.emit(data.unassignedPayload);
        }
      })
    }

  }

  getFieldValue<T>(field: FieldValueModel<T>): T | undefined {
    if (!field) {
      return undefined;
    } else if (isUndefined(field.adjusted?.value)) {
      return field.initial?.value
    } else {
      return field.adjusted?.value
    }
  }

  hasAdjustedDeliveryUnits(): boolean {
    const initial = this.cellCopy?.deliveryDetails?.deliveryUnits?.initial?.value;
    const adjusted = this.cellCopy?.deliveryDetails?.deliveryUnits?.adjusted?.value;
    return adjusted !== undefined && adjusted !== null && adjusted !== initial;
  }

  private _setIsDayDispatched() {
    const cellDay = findVisitScheduleDayByDate(this.visitScheduleDays, this.cell?.date) || this.visitScheduleDay;
    this.isDayDispatched = isVisitScheduleDayDispatched(cellDay);
  }

  private _setCellDeliveryEtdWarning() {
    const deliveryEtd = this.getFieldValue(this.cell.deliveryDetails.deliveryEtd);

    const isDeliveryEtdSameDay = deliveryEtd && this.visitScheduleDay?.date ?
      moment(moment(this.visitScheduleDay.date).format("MM/DD/YYYY")).isSame(moment(deliveryEtd).format("MM/DD/YYYY")) :
      false;

    this.isDeliveryEtdNextDay = deliveryEtd && this.visitScheduleDay?.date ?
      (!isDeliveryEtdSameDay && deliveryEtd > this.visitScheduleDay.date) :
      false;

    this.isDeliveryEtdPreviousDay = deliveryEtd && this.visitScheduleDay?.date ?
      (!isDeliveryEtdSameDay && deliveryEtd < this.visitScheduleDay.date) :
      false;
  }

  private initCellTotals() {
    this.cellCopy = !!this.cellTracker ? {...this.cellTracker} : {...this.cell};
    this.cellTotals = {
      merchandiseCount: 0,
      ordersCount: 0,
      salesCount: 0,
      initialServiceTime: 0,
      adjustedServiceTime: 0,
      merchandiseUnitsCount: 0,
      hasAdjustedServiceTime: false
    };
    this.cellCopy.calls.sort((a, b) => this.programTypeAliasMap[this.getFieldValue(a.programType)].localeCompare(this.programTypeAliasMap[this.getFieldValue(b.programType)]));

    this.hasDelivery = !!this.cellCopy.deliveryDetails && this.getFieldValue(this.cellCopy.deliveryDetails.isDeliveryDay);

    this.cellCopy.calls?.forEach(call => {

      if (this.programTypeAliasMap[this.getFieldValue(call.programType)] === 'M') {
        this.cellTotals.merchandiseCount += 1;
      }
      if (this.programTypeAliasMap[this.getFieldValue(call.programType)] === 'O') {
        this.cellTotals.ordersCount += 1;
      }
      if (this.programTypeAliasMap[this.getFieldValue(call.programType)] === 'S') {
        this.cellTotals.salesCount += 1;
      }


      if (!!call.origin) {
        this.cellTotals.initialServiceTime += call.origin.serviceTime?.initial?.value?.generatedTotalTime || 0;

        if (call.origin.serviceTime?.adjusted?.value?.generatedTotalTime) {
          this.cellTotals.adjustedServiceTime += call.origin.serviceTime?.adjusted?.value?.generatedTotalTime || 0;
        } else {
          this.cellTotals.adjustedServiceTime += call.origin.serviceTime?.initial?.value?.generatedTotalTime || 0;
        }
      }

      if (!!call.destination) {
        this.cellTotals.initialServiceTime += call.destination.serviceTime?.initial?.value?.generatedTotalTime || 0;

        if (call.destination.serviceTime?.adjusted?.value?.generatedTotalTime) {
          this.cellTotals.hasAdjustedServiceTime = true;
          this.cellTotals.adjustedServiceTime += call.destination.serviceTime?.adjusted?.value?.generatedTotalTime || 0;
        } else {
          this.cellTotals.adjustedServiceTime += call.destination.serviceTime?.initial?.value?.generatedTotalTime || 0;
        }

        if (!!call.units) {
          this.cellTotals.merchandiseUnitsCount += this.getFieldValue(call.units) || 0;
        }
      }

    })

  }
}
